import { useEffect, useRef, useState } from "react";
import useApi from "./useApi";

export const PREVIEW_SIZE = 6;

/**
 * One-shot loader for a landing-page preview strip: asks the endpoint for the
 * first `size` records only, instead of pulling the whole list down and slicing
 * it in the browser.
 *
 * `hasMore` is true when the response suggests there is more behind the
 * "SEE ALL" link — either an explicit total, or a full page coming back.
 */
export default function usePreviewList({
  endpoint,
  method = "post",
  size = PREVIEW_SIZE,
  selectItems = (result) => result?.data,
}) {
  const { trigger } = useApi();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);

  // `trigger` is a new closure each render; keep it out of the effect deps.
  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;

  useEffect(() => {
    // No "already started" ref guard here: StrictMode mounts, cleans up, then
    // mounts again. A ref guard would skip the second run while the first run's
    // cleanup had already flagged its response as stale, so the result would be
    // thrown away and nothing would ever render. The `cancelled` flag alone is
    // the correct pattern - the second mount issues its own live request.
    let cancelled = false;

    (async () => {
      const separator = endpoint.includes("?") ? "&" : "?";
      const result = await triggerRef.current({
        endpoint: `${endpoint}${separator}limit=${size}&offset=0`,
        method,
        requireAuth: false,
      });

      if (cancelled) return;

      if (String(result?.sts) !== "0") {
        console.error(result?.msg);
        setLoading(false);
        return;
      }

      const page = selectItems(result);
      const pageItems = Array.isArray(page) ? page : [];
      // `count` is left out on purpose - see readTotal in useInfiniteList.
      const reported = result?.total ?? result?.totalCount ?? result?.totalRecords;
      const total = reported === undefined || reported === null ? NaN : Number(reported);

      setItems(pageItems.slice(0, size));
      setHasMore(
        Number.isFinite(total) ? total > size : pageItems.length >= size
      );
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [endpoint, method, size, selectItems]);

  return { items, loading, hasMore };
}
